import { useEffect, useState } from 'react'
import { RefreshCw, ShieldAlert } from 'lucide-react'
import { useRanking, useRecomputeRanking, type RankingOut } from '@/api/endpoints/ranking'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { Tooltip } from '@/components/ui/Tooltip'
import { EmptyState } from '@/components/ds/EmptyState'
import { RankingBreakdownModal } from '@/features/ranking/components/RankingBreakdownModal'
import type { RankingBreakdownTab } from '@/features/ranking/components/RankingBreakdownView'
import { cn } from '@/lib/cn'
import styles from './RankingDock.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface RankingDockProps {
  videoId: string
  lang: string
}

/**
 * Score explainer for the Project Detail. Shows the final composite score
 * plus the three major sub-scores (heuristic B, LLM judge, performance
 * multiplier) so the user can tell *why* a video ended up where it did.
 *
 * Reads from ``/api/v1/ranking/{video_id}`` — which is richer than the slim
 * ``video.ranking`` included in the list payload. Lets the user trigger a
 * recompute when they've edited metadata or the criteria version bumped.
 */
export function RankingDock({ videoId, lang }: RankingDockProps) {
  // Set when the user kicks off a recompute. While non-null, useRanking
  // polls every 2s until the worker writes a row newer than this stamp;
  // we then clear it so the query goes back to its idle 30s staleTime.
  const [awaitNewerThan, setAwaitNewerThan] = useState<string | null>(null)
  const [modalTab, setModalTab] = useState<RankingBreakdownTab | null>(null)
  const { data: ranking, isPending, isError, error, refetch } = useRanking({
    videoId,
    lang,
    awaitNewerThan,
  })
  const recompute = useRecomputeRanking()

  useEffect(() => {
    if (!awaitNewerThan) return
    if (ranking && ranking.computed_at && ranking.computed_at > awaitNewerThan) {
      setAwaitNewerThan(null)
    }
  }, [ranking, awaitNewerThan])

  const handleRecompute = () => {
    // Capture the trigger timestamp BEFORE firing the mutation so the
    // poller has a stable cutoff even if the worker is fast enough to
    // beat the mutation onSuccess to the cache.
    const triggeredAt = new Date().toISOString()
    setAwaitNewerThan(triggeredAt)
    recompute.mutate({ videoId, lang })
  }

  if (isPending) {
    return <div className={styles.missing}>Carregando ranking...</div>
  }
  if (isError) {
    return (
      <QueryErrorNotice
        error={error}
        label="o ranking"
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }
  if (!ranking) {
    return (
      <EmptyState
        icon={<ShieldAlert />}
        title="Sem ranking ainda"
        description="Este vídeo ainda não foi pontuado. Rode um recompute para gerar o score."
        action={
          <Button
            leadingIcon={<RefreshCw />}
            onClick={handleRecompute}
            disabled={recompute.isPending || awaitNewerThan !== null}
          >
            {recompute.isPending
              ? 'Enfileirando...'
              : awaitNewerThan
                ? 'Calculando...'
                : 'Calcular ranking'}
          </Button>
        }
      />
    )
  }

  const finalScore = ranking.final_score
  const scoreClass =
    finalScore === null
      ? styles.scoreBigBad
      : finalScore >= 0.65
        ? styles.scoreBigGood
        : finalScore >= 0.4
          ? styles.scoreBigOk
          : styles.scoreBigBad

  const groupAScore = extractGroupAScore(ranking.breakdown)
  const guardrails = ranking.guardrails_violated

  return (
    <div className={styles.shell}>
      <div className={styles.scoreCard}>
        <div className={styles.scoreCol}>
          <span className={cn(styles.scoreBig, scoreClass)}>{formatScore(finalScore)}</span>
          <span className={styles.scoreLabel}>Final score</span>
        </div>
        <div className={styles.scoreMetaCol}>
          <div className={styles.scoreChipRow}>
            {ranking.quarantined ? (
              <Chip variant="danger">Em quarentena</Chip>
            ) : finalScore !== null && finalScore < 0.4 ? (
              <Chip variant="warning">Score baixo</Chip>
            ) : (
              <Chip variant="success">Elegível</Chip>
            )}
          </div>
          <span className={styles.scoreMeta}>Critérios {ranking.criteria_version}</span>
          <span className={styles.scoreMeta}>
            {ranking.computed_at
              ? new Date(ranking.computed_at).toLocaleString('pt-BR')
              : 'sem data'}
          </span>
        </div>
      </div>

      <section className={styles.section}>
        <span className={styles.sectionTitle}>Breakdown</span>
        <div className={styles.breakdownGrid}>
          <Metric
            label="Grupo A"
            tooltip="Guardrails (hook, hashtags, etc.). Violação capa o score em 0.2. Clique para detalhar."
            value={formatScore(groupAScore)}
            meta={guardrails.length > 0 ? `${String(guardrails.length)} violação(ões)` : 'limpo'}
            onClick={() => { setModalTab('group_a') }}
          />
          <Metric
            label="Grupo B"
            tooltip="Sinais emergentes: cadência visual, novelty, freshness, factory-specific. Clique para detalhar."
            value={formatScore(ranking.heuristic_b_score)}
            onClick={() => { setModalTab('group_b') }}
          />
          <Metric
            label="Juiz (LLM)"
            tooltip="Dimensões qualitativas que o generator não controla: ressonância emocional, surpresa. Clique para detalhar."
            value={formatScore(ranking.judge_score)}
            onClick={() => { setModalTab('judge') }}
          />
          <Metric
            label="× Performance"
            tooltip="Multiplicador derivado do YouTube Analytics agregado do canal. Clique para detalhar."
            value={formatMultiplier(ranking.perf_multiplier)}
            meta={
              ranking.perf_multiplier === 1
                ? 'neutro'
                : ranking.perf_multiplier > 1
                  ? 'boost'
                  : 'penalidade'
            }
            onClick={() => { setModalTab('performance') }}
          />
        </div>
      </section>

      {modalTab !== null ? (
        <RankingBreakdownModal
          open
          initialTab={modalTab}
          ranking={ranking}
          onClose={() => { setModalTab(null) }}
        />
      ) : null}

      {guardrails.length > 0 ? (
        <section className={styles.section}>
          <span className={styles.sectionTitle}>Guardrails violados</span>
          <div className={styles.guardrails}>
            {guardrails.map((g) => (
              <Chip key={g} variant="danger">
                {g}
              </Chip>
            ))}
          </div>
        </section>
      ) : null}

      <section className={styles.section}>
        <span className={styles.sectionTitle}>Contexto</span>
        <div className={styles.breakdownGrid}>
          <Metric label="Factory" value={ranking.factory} long />
          <Metric label="Canal" value={ranking.channel_slug} long />
          <Metric
            label="Cortes visuais"
            value={ranking.visual_cuts === null ? '—' : String(ranking.visual_cuts)}
          />
          {ranking.pattern ? <Metric label="Padrão" value={ranking.pattern} long /> : null}
        </div>
      </section>

      <div className={styles.cta}>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<RefreshCw />}
          onClick={handleRecompute}
          disabled={recompute.isPending || awaitNewerThan !== null}
        >
          {recompute.isPending
            ? 'Enfileirando...'
            : awaitNewerThan
              ? 'Recalculando...'
              : 'Recomputar ranking'}
        </Button>
      </div>
    </div>
  )
}

interface MetricProps {
  label: string
  value: string
  meta?: string
  tooltip?: string
  /** Use a smaller sans font + truncate for long free-form strings
   * (factory slugs, channel names) so the metric cell doesn't overflow. */
  long?: boolean
  /** When set, the metric renders as a clickable button — used by the
   *  breakdown cards to open the detail modal. Without it, the cell
   *  remains a plain ``<div>`` (e.g. context cards like "Factory"). */
  onClick?: () => void
}

function Metric({ label, value, meta, tooltip, long, onClick }: MetricProps) {
  const inner = (
    <>
      <span className={styles.metricLabel}>{label}</span>
      <span className={cn(styles.metricValue, long && styles.metricValueLong)} title={long ? value : undefined}>
        {value}
      </span>
      {meta ? <span className={styles.metricMeta}>{meta}</span> : null}
    </>
  )
  const content = onClick ? (
    <button
      type="button"
      className={cn(styles.metric, styles.metricButton)}
      onClick={onClick}
    >
      {inner}
    </button>
  ) : (
    <div className={styles.metric}>{inner}</div>
  )
  if (!tooltip) return content
  return <Tooltip content={tooltip}>{content}</Tooltip>
}

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—'
  return score.toFixed(2)
}

function formatMultiplier(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—'
  return `${m.toFixed(2)}×`
}

/**
 * The breakdown dict shape isn't strictly typed — it mirrors the scorer
 * output which evolves with ``criteria_version``. We probe the common
 * paths for the Group A subscore; callers fall back to "—" if missing.
 */
function extractGroupAScore(breakdown: RankingOut['breakdown']): number | null {
  const groupA = breakdown['group_a']
  if (groupA && typeof groupA === 'object') {
    const score = (groupA as Record<string, unknown>)['score']
    if (typeof score === 'number') return score
  }
  const guardrails = breakdown['guardrails_score']
  if (typeof guardrails === 'number') return guardrails
  return null
}
